import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// 🏷️ Nomes amigáveis pros toasts de módulo bloqueado (fallback = o nome técnico mesmo)
const MODULE_FRIENDLY_NAMES = {
  security_cameras: 'Câmeras de Segurança',
  instagram_giveaway: 'Sorteio do Instagram',
  aria_ai: 'Assistente Aria',
  kitchen_labels: 'Etiquetas de Cozinha',
};

/**
 * Componente de proteção de rotas baseado em módulos habilitados
 * Verifica se a empresa tem acesso ao módulo antes de renderizar a página
 * 
 * @param {string} module - Nome do módulo (ex: "financial", "fiscal", "stock")
 * @param {ReactNode} children - Componente filho a ser renderizado se tiver acesso
 */
const ModuleGuard = ({ module, children }) => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null); // null = verificando, true = tem acesso, false = sem acesso

  useEffect(() => {
    // Verificar acesso IMEDIATAMENTE
    const checkAccess = () => {
      // Buscar dados do usuário do localStorage
      const userData = localStorage.getItem('user');
      
      if (!userData) {
        // Se não estiver logado, redirecionar para login
        setHasAccess(false);
        navigate('/admin/login');
        return;
      }

      try {
        const user = JSON.parse(userData);
        const company = user?.company;
        
        if (!company) {
          // Usuário sem empresa
          setHasAccess(false);
          toast.error('Você não está vinculado a uma empresa');
          navigate('/admin/welcome');
          return;
        }

        const modulesEnabled = company?.modules_enabled || {};
        
        // Verificar se o módulo está HABILITADO (deve ser true explicitamente)
        // Módulos não configurados ou false = sem acesso
        const moduleIsEnabled = modulesEnabled[module] === true;
        
        if (!moduleIsEnabled) {
          console.warn(`🚫 Acesso negado ao módulo '${module}' - Módulo não habilitado para esta empresa`);
          setHasAccess(false);
          toast.error(`Módulo "${MODULE_FRIENDLY_NAMES[module] || module}" não está disponível para sua empresa. Entre em contato com o administrador.`);
          navigate('/admin/welcome', { replace: true });
          return;
        }

        // Se chegou aqui, tem acesso (módulo explicitamente habilitado)
        console.log(`✅ Acesso permitido ao módulo '${module}'`);
        setHasAccess(true);
        
      } catch (error) {
        console.error('Erro ao verificar permissões de módulo:', error);
        setHasAccess(false);
        navigate('/admin/login');
      }
    };

    checkAccess();
  }, [module, navigate]);

  // Não renderizar nada enquanto verifica
  if (hasAccess === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Verificando acesso...</div>
      </div>
    );
  }

  // Se não tem acesso, não renderizar
  if (hasAccess === false) {
    return null;
  }

  // Se tem acesso, renderizar children
  return <>{children}</>;
};

export default ModuleGuard;
